import styled from 'styled-components'

import { ClinTheme } from '../../../ClinTheme'
import { ClinText } from '../../../components/ClinText'
import { mediaQueryDown } from '../../../utils/mediaQuery'
import { ClinButton } from '../../../components/ClinButton'

interface StyledAccountDetailsSummaryItemProps {
  paddingRight?: boolean
  paddingLeft?: boolean
  borderRight?: boolean
}

interface StyledShippingAddressItemProps {
  borderRadius?: string
}

interface StyledFieldWrapperProps {
  isEdit?: boolean
}

interface StyledRowTitleProps {
  isEdit?: boolean
}

const tabletBreakpoint = ClinTheme.breakpoints[2]

export const StyledPhoneExtension = styled.span({
  display: 'inline-block',
  marginRight: `6px`
})

export const StyledPhoneExtensionNumber = styled.span({
  fontWeight: `${ClinTheme.fontWeights.bold}`,
  fontSize: `${ClinTheme.fontSizes[3]}`
})

export const AccountDetailsHeaderContainer = styled.div({
  width: '100%',
  margin: '0 auto',
  backgroundColor: ClinTheme.colors.white
})

export const StyledAccountDetailsHeaderTitle = styled(ClinText)({
  fontFamily: ClinTheme.fonts.groteskExtraBold,
  color: ClinTheme.colors.black,
  fontWeight: ClinTheme.fontWeights.bolder
})

const styledText = {
  color: ClinTheme.colors.black,
  fontSize: ClinTheme.fontSizes[1],
  fontWeight: ClinTheme.fontWeights.medium,
  lineHeight: '20px',
  letterSpacing: 0,
  padding: 0
}

export const StyledAccountDetailsSummaryItem =
  styled.div<StyledAccountDetailsSummaryItemProps>(
    ({ paddingRight, paddingLeft, borderRight }) => ({
      flex: '1',
      paddingRight: paddingRight ? ClinTheme.space[4] : '0',
      paddingLeft: paddingLeft ? ClinTheme.space[4] : '0',
      borderRight: borderRight
        ? `1px solid ${ClinTheme.colors.midGrey}`
        : 'none',

      [mediaQueryDown(tabletBreakpoint)]: {
        border: 'none',
        padding: ClinTheme.space[0],

        '&:not(:last-child)': {
          marginBottom: ClinTheme.space[5]
        }
      },

      '&:first-child': {
        flex: '0 0 auto',

        [mediaQueryDown(tabletBreakpoint)]: {
          marginBottom: ClinTheme.space[3],
          paddingBottom: ClinTheme.space[3],
          borderBottom: `1px solid ${ClinTheme.colors.midGrey}`
        }
      }
    })
  )

export const StyledAccountDetailsAvatar = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
  color: ClinTheme.colors.black,
  fontSize: ClinTheme.fontSizes[5],
  fontWeight: ClinTheme.fontWeights.bolder,
  lineHeight: '40px',
  width: '88px',
  height: '88px',
  textAlign: 'center',
  backgroundColor: '#FFBDA2',
  marginRight: '8px',

  [mediaQueryDown(tabletBreakpoint)]: {
    width: '32px',
    height: '32px',
    fontSize: ClinTheme.fontSizes[0]
  }
})

export const StyledRow = styled.div({
  '&.not-editing': {
    input: {
      border: 'none',
      ...styledText,
      '&:focus': {
        border: 'none',
        outline: 'none',
        boxShadow: 'none'
      }
    }
  },

  '&:not(:last-child)': {
    marginBottom: ClinTheme.space[3]
  }
})

export const StyledRowTitle = styled(ClinText)<StyledRowTitleProps>(
  ({ isEdit }) => ({
    color: isEdit ? ClinTheme.colors.black : ClinTheme.colors.darkGrey,
    fontSize: isEdit ? ClinTheme.fontSizes[1] : ClinTheme.fontSizes[0],
    fontWeight: isEdit
      ? ClinTheme.fontWeights.normal
      : ClinTheme.fontWeights.medium,
    lineHeight: isEdit
      ? ClinTheme.lineHeights.small
      : ClinTheme.lineHeights.body,
    letterSpacing: 0,
    ...(isEdit && { marginBottom: ClinTheme.space[2] })
  })
)

export const StyledRowText = styled.div(styledText)

export const StyledAccountDetailsSummary = styled.div({
  width: '100%',
  padding: ClinTheme.space[4],
  backgroundColor: ClinTheme.colors.white,
  borderRadius: 4,
  boxSizing: 'border-box',

  [mediaQueryDown(tabletBreakpoint)]: {
    padding: ClinTheme.space[3]
  },

  '.inactive': {
    border: 'none',
    outline: 'none',

    input: {
      width: '100%'
    }
  },

  '&.editing': {
    [`${StyledAccountDetailsSummaryItem}`]: {
      flex: '0 0 auto',
      [mediaQueryDown(tabletBreakpoint)]: {
        marginBottom: ClinTheme.space[0]
      }
    },
    [`${StyledAccountDetailsAvatar}`]: {
      display: 'none'
    },
    [`${StyledRowTitle}`]: {
      color: ClinTheme.colors.black,
      fontSize: ClinTheme.fontSizes[1],
      fontWeight: ClinTheme.fontWeights.normal,
      lineHeight: '16px',
      marginBottom: ClinTheme.space[2]
    },
    [`${StyledAccountDetailsSummaryItem}:first-child`]: {
      borderBottom: 'none',
      paddingBottom: 0
    }
  }
})

export const StyledAccountDetailsSummaryContainer = styled.div({
  display: 'flex',

  [mediaQueryDown(tabletBreakpoint)]: {
    flexDirection: 'column'
  }
})

export const StyledUserName = styled(ClinText)({
  position: 'relative',
  fontWeight: ClinTheme.fontWeights.medium,
  color: ClinTheme.colors.primaryLight,
  paddingLeft: ClinTheme.space[2],

  '&::before': {
    content: '"•"',
    position: 'absolute',
    top: 0,
    left: '-7px'
  }
})

export const StyledAccountDetailsBar = styled.div({
  padding: `${ClinTheme.space[4]}px ${ClinTheme.space[3]}px`,
  width: '100%',
  borderRadius: '4px 4px 0px 0px',
  backgroundColor: ClinTheme.colors.white,
  boxSizing: 'border-box',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: ClinTheme.space[2],

  [mediaQueryDown(tabletBreakpoint)]: {
    gridTemplateColumns: '1fr'
  }
})

export const StyledUsersBar = styled.div({
  display: 'flex',
  alignItems: 'center',
  padding: ClinTheme.space[3],
  backgroundColor: ClinTheme.colors.white,
  borderRadius: '4px 4px 0px 0px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
})

export const StyledCountBadge = styled.span({
  display: 'flex',
  alignItems: 'center',
  fontSize: '10px',
  fontWeight: ClinTheme.fontWeights.medium,
  padding: `0 6px`,
  borderRadius: '13px',
  background: '#EDEDFC',
  marginLeft: ClinTheme.space[2],
  height: '16px'
})

export const StyledUsersShippingContainer = styled.div({
  display: 'flex',
  gap: ClinTheme.space[3],

  [mediaQueryDown(tabletBreakpoint)]: {
    flexDirection: 'column'
  }
})

export const StyledUsers = styled.div({
  width: 'calc(100% * 7 / 12)',

  [mediaQueryDown(tabletBreakpoint)]: {
    width: '100%'
  }
})

export const StyledShippingAddress = styled.div({
  width: 'calc(100% * 5 / 12)',

  [mediaQueryDown(tabletBreakpoint)]: {
    width: '100%'
  }
})

export const StyledShippingAddressBar = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${ClinTheme.space[4]}px ${ClinTheme.space[3]}px`,
  backgroundColor: ClinTheme.colors.white,
  borderRadius: '4px 4px 0px 0px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  gap: ClinTheme.space[5],

  [mediaQueryDown(tabletBreakpoint)]: {
    alignItems: 'flex-start',
    gap: ClinTheme.space[3],
    flexDirection: 'column'
  }
})

export const StyledShippingAddressItem =
  styled.div<StyledShippingAddressItemProps>(({ borderRadius }) => ({
    padding: ClinTheme.space[4],
    backgroundColor: ClinTheme.colors.white,
    borderRadius: borderRadius || '0px',

    [mediaQueryDown(tabletBreakpoint)]: {
      padding: ClinTheme.space[3]
    }
  }))

export const StyledShippingAddressSubBar = styled.div({
  display: 'flex',
  alignItems: 'center',
  padding: ClinTheme.space[3],
  backgroundColor: ClinTheme.colors.white,
  borderRadius: '4px 4px 0px 0px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
})

export const StyledPrimaryBadge = styled.span({
  fontSize: ClinTheme.fontSizes[0],
  fontWeight: ClinTheme.fontWeights.medium,
  padding: `${ClinTheme.space[1]}px ${ClinTheme.space[2]}px`,
  color: ClinTheme.colors.greenValid,
  backgroundColor: ClinTheme.colors.lightenGreen,
  borderRadius: ClinTheme.space[2],
  marginLeft: ClinTheme.space[2]
})

export const FieldWrapper = styled.div<StyledFieldWrapperProps>(({ isEdit }) =>
  isEdit
    ? {
        marginBottom: '1rem',
        backgroundColor: ClinTheme.colors.lightGrey,
        borderRadius: ClinTheme.radii[3],
        padding: ClinTheme.space[3],
        gap: ClinTheme.space[3]
      }
    : {}
)

export const StyledClinPrimaryButton = styled(ClinButton)({
  fontSize: ClinTheme.fontSizes[2],
  paddingRight: ClinTheme.space[4],
  paddingLeft: ClinTheme.space[4],
  letterSpacing: 0,
  height: '44px',
  flex: '1 1 auto',
  maxWidth: '86px',
  [mediaQueryDown(tabletBreakpoint)]: {
    maxWidth: '100%'
  }
})

export const StyledClinSecondaryButton = styled(ClinButton)({
  color: ClinTheme.colors.black,
  fontSize: ClinTheme.fontSizes[2],
  paddingRight: ClinTheme.space[4],
  paddingLeft: ClinTheme.space[4],
  letterSpacing: 0,
  height: '44px',
  backgroundColor: 'transparent',
  flex: '1 1 auto',
  maxWidth: '120px',
  '&:disabled': {
    color: ClinTheme.colors.darkGrey
  },
  [mediaQueryDown(tabletBreakpoint)]: {
    flex: '1 1 50%',
    maxWidth: '100%'
  }
})

export const StyledClinPrimaryButton2 = styled(ClinButton)({
  fontSize: ClinTheme.fontSizes[2],
  paddingRight: ClinTheme.space[4],
  paddingLeft: ClinTheme.space[4],
  letterSpacing: 0,
  height: '44px',
  flex: '1 1 auto',
  maxWidth: '175px',
  [mediaQueryDown(tabletBreakpoint)]: {
    maxWidth: '100%'
  }
})

export const StyledClinSecondaryButton2 = styled(ClinButton)({
  color: ClinTheme.colors.black,
  fontSize: ClinTheme.fontSizes[2],
  paddingRight: ClinTheme.space[4],
  paddingLeft: ClinTheme.space[4],
  letterSpacing: 0,
  height: '40px',
  backgroundColor: 'transparent',
  flex: '1 1 auto',
  '&:disabled': {
    color: ClinTheme.colors.darkGrey
  }
})

export const StyledLoadingContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  flex: '1 1 auto',
  [mediaQueryDown(tabletBreakpoint)]: {
    flex: '1 1 50%'
  }
})

export const StyledAccountDetailsAddress = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${ClinTheme.space[4]}px`,
  backgroundColor: ClinTheme.colors.white,
  borderRadius: '4px 4px 0px 0px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  gap: ClinTheme.space[5],

  [mediaQueryDown(tabletBreakpoint)]: {
    alignItems: 'flex-start',
    gap: ClinTheme.space[3],
    flexDirection: 'column'
  }
})
